import { createRef, FC, Fragment, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import kebabCase from 'lodash/kebabCase';
import { FEATURES } from '@belong/feature-toggles/constants';
import { useFeatureToggles } from '@belong/feature-toggles/hooks';
import useRuntime from '@belong/providers/runtime';
import { SearchPanel } from '@belong/search';
import { ICON_NAMES, IOrganismMenuItem, USER_TYPE } from '@belong/types';
import { A11yCopy, AccentStrip, Logo as BelongLogo, DropdownWrapper, KEYBOARD_KEYS } from '@belong/ui-components';
import { removeTrailingSlash } from '@belong/url-utils';
import { getVisibleHeaderMenuItems } from '../menuUtils';
import { NbnCartButton } from '../NbnCartButton';
import { GLOBAL_HEADER_BUTTON_PREFIX, GLOBAL_HEADER_TEST_ID as TEST_ID } from '../testids';
import AgentHeader from './AgentHeader';
import HeaderBarButton from './HeaderBarButton';
import { HeaderDropdown } from './HeaderDropdown';
import * as styles from './styles';
import { CONTAINERS, IGlobalHeaderDesktop, THeaderDropdownWrapper } from './types';

interface IRestoreFocusOptions {
  nextMenuItem?: boolean;
  previousMenuItem?: boolean;
}

const SEARCH_PANEL_A11Y_ID = 'search-panel-a11y-id';
const SEARCH_TITLE_A11Y_ID = 'search-title-a11y-id';
const MY_ACCOUNT_PANEL_A11Y_ID = 'myaccount-panel-a11y-id';

export const GlobalHeaderDesktop: FC<IGlobalHeaderDesktop> = ({
  headerMenuContent,
  currentUrl,
  isLoggedIn,
  userType,
  hideMyAccountLinks,
  urls,
  onExpanded,
  isNbnOrderCartReady,
  navHeaderRef
}) => {
  const [dropdownContainer, setDropdownContainer] = useState<THeaderDropdownWrapper>(CONTAINERS.CONTAINER_MAIN);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [menuContent, setMenuContent] = useState<IOrganismMenuItem | undefined>(undefined);
  const currentMenuItemIdx = useRef<number | undefined>(undefined);
  const [menuItemID, setMenuItemID] = useState<string | undefined>(undefined);
  const accountMenuButtonRef = useRef<HTMLButtonElement>(null);
  const { isDesktopNav } = useRuntime();
  const { isFeatureOn } = useFeatureToggles();

  const isUserAgent = userType === USER_TYPE.AGENT;
  const visibleHeaderMenuItems = getVisibleHeaderMenuItems(
    isLoggedIn,
    isFeatureOn(FEATURES.AUTHENTICATED_SHOP),
    headerMenuContent?.headerMenuItems
  );
  const menuItemRef = useRef<RefObject<HTMLButtonElement | null>[]>(createMenuItemRefs());

  const pressedKeys = useRef<string[]>([]);
  const KEY_MODIFIER = 'Shift';

  useEffect(() => {
    onExpanded?.(isDropdownOpen);
  }, [onExpanded, isDropdownOpen]);

  function createMenuItemRefs() {
    const refs: RefObject<HTMLButtonElement | null>[] = [];
    if (headerMenuContent) {
      visibleHeaderMenuItems.forEach(() => {
        refs.push(createRef<HTMLButtonElement>());
      });

      // push an extra ref for the search button
      refs.push(createRef<HTMLButtonElement>());
    }

    return refs;
  }

  const focusMenuItem = (menuItemIdx: number) => {
    menuItemRef?.current[menuItemIdx]?.current?.focus();
  };

  const parseMenuItemLabel = (label: string) => {
    return `${GLOBAL_HEADER_BUTTON_PREFIX}${kebabCase(label).toLowerCase()}`;
  };

  const getMainOrSearchMenuItemId = (nextMenuItem: boolean, menuIndex: number): string => {
    let menuItem = '';

    if (menuIndex < visibleHeaderMenuItems.length) {
      menuItem =
        visibleHeaderMenuItems[nextMenuItem ? menuIndex + 1 : menuIndex]?.parentLabel ??
        headerMenuContent.searchMenuLabel;
    } else {
      menuItem = headerMenuContent.searchMenuLabel;
    }

    return parseMenuItemLabel(menuItem);
  };

  const getFocusMenuItem = ({ nextMenuItem = false, previousMenuItem = false }: IRestoreFocusOptions = {}): string => {
    if (currentMenuItemIdx.current === undefined) {
      return '';
    }

    let menuItem = '';

    // menu items which don't have children are wrapped with hasLink meaning they don't support refs
    // use the id instead so that the focus can be managed by all the buttons in the header
    if (nextMenuItem && currentMenuItemIdx.current === visibleHeaderMenuItems.length) {
      menuItem = parseMenuItemLabel(headerMenuContent.loginLabel); // at the end of the menu so focus on login link
      setDropdownContainer(CONTAINERS.CONTAINER_ACCOUNT);
    } else {
      // this gets a little complex because the search menu item is not part of the headerMenuItems array
      // the blurMenuItem is set to the search menu item if we're at the end of the header menu
      // otherwise it's set to the current menu item or the next header menu item if the 'nextMenuItem' argument is true
      menuItem = getMainOrSearchMenuItemId(nextMenuItem, currentMenuItemIdx.current);

      if (nextMenuItem) {
        currentMenuItemIdx.current += 1;
      }
    }

    if (previousMenuItem) {
      menuItem = parseMenuItemLabel(
        currentMenuItemIdx.current > 1
          ? visibleHeaderMenuItems[currentMenuItemIdx.current - 1].parentLabel
          : headerMenuContent.belongHome.label
      );
    }

    return menuItem;
  };

  /**
   *
   * The purpose of this function is to determine where focus should go at any given time, mostly after the menu closing.
   * For now it is a little complex due to the feature toggle. After the toggle is removed it would be worth revisiting the logic to find ways of simplifying it.
   *
   * @nextMenuItem - if true, the focus will move to the next menu item
   * @previousMenuItem - if true, the focus will move to the previous menu item
   */

  const restoreFocus = (options?: IRestoreFocusOptions) => {
    if (!headerMenuContent || currentMenuItemIdx.current === undefined) {
      return;
    }

    // this finds the nav bar item that focus should return to
    let onBlurMenuItem = '';
    if (dropdownContainer === CONTAINERS.CONTAINER_MAIN || dropdownContainer === CONTAINERS.CONTAINER_SEARCH) {
      onBlurMenuItem = getFocusMenuItem(options);
    }

    if (dropdownContainer === CONTAINERS.CONTAINER_ACCOUNT) {
      onBlurMenuItem = parseMenuItemLabel(headerMenuContent.loginLabel); // if we're not a headerMenuItem we must be the login link
      setIsDropdownOpen(false);
    }

    document.getElementById(onBlurMenuItem)?.focus();
  };

  const closeHeader = useCallback(
    ({ previousMenuItem = false }: { previousMenuItem?: boolean } = {}) => {
      if (isDropdownOpen) {
        (window.document.activeElement as HTMLElement)?.blur?.();
        restoreFocus({ nextMenuItem: false, previousMenuItem });

        setIsDropdownOpen(false);
      }
    },
    [isDropdownOpen, restoreFocus]
  );

  useEffect(() => {
    if (!isDesktopNav) {
      closeHeader();
    }
  }, [closeHeader, isDesktopNav]);

  const handleEventMenuWithChildren = (menuItemIdx: number, newMenuContent: IOrganismMenuItem) => {
    if (isDropdownOpen) {
      if (currentMenuItemIdx.current === menuItemIdx) {
        closeHeader();
        return;
      }
    } else {
      setIsDropdownOpen(true);
    }

    setMenuItemID(parseMenuItemLabel(newMenuContent.parentLabel));
    currentMenuItemIdx.current = menuItemIdx;

    setDropdownContainer(CONTAINERS.CONTAINER_MAIN);
    focusMenuItem(menuItemIdx);
    setMenuContent(newMenuContent);
  };

  const handleSearchButtonClick = (menuItemIdx: number) => {
    if (isDropdownOpen) {
      if (currentMenuItemIdx.current === menuItemIdx) {
        closeHeader();
        return;
      }
    } else {
      setIsDropdownOpen(true);
    }

    setMenuItemID(parseMenuItemLabel(headerMenuContent.searchMenuLabel));
    currentMenuItemIdx.current = menuItemIdx;

    setDropdownContainer(CONTAINERS.CONTAINER_SEARCH);
    focusMenuItem(menuItemIdx);
  };

  const handleEventAccountMenu = () => {
    (window.document.activeElement as HTMLElement)?.blur?.();
    currentMenuItemIdx.current = visibleHeaderMenuItems.length + 1;
    setDropdownContainer(CONTAINERS.CONTAINER_ACCOUNT);
    setIsDropdownOpen(true);
    accountMenuButtonRef.current?.focus();
  };

  const handleKeyPress = useCallback(
    ({ key }: KeyboardEvent): void => {
      if (key === 'Escape') {
        restoreFocus();
        closeHeader();
      }
    },
    [restoreFocus, closeHeader]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  // the onLastLinkBlur function is called for keyboard interaction when the focus leaves forward from the last link in the dropdown
  const onLastLinkBlur = (): void => {
    if (pressedKeys.current.pop() === 'Tab') {
      closeHeader();
      restoreFocus({ nextMenuItem: dropdownContainer === CONTAINERS.CONTAINER_MAIN });
      pressedKeys.current = [];
    }
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement | HTMLButtonElement>): void => {
    const { key } = event;

    // Save pressed keys when focus is in the last active item within the get connected section
    if (
      key === KEYBOARD_KEYS.TAB ||
      key === KEYBOARD_KEYS.ENTER ||
      (event.getModifierState(KEY_MODIFIER) && key === KEYBOARD_KEYS.TAB)
    ) {
      pressedKeys.current.push(event.getModifierState(KEY_MODIFIER) ? `${KEY_MODIFIER}+${key}` : key);
    }
  };

  return (
    <styles.Root data-testid={TEST_ID.DESKTOP}>
      <styles.Screen
        data-testid={TEST_ID.OFFCANVAS}
        data-body-scroll={isDropdownOpen && isDesktopNav ? 'locked' : ''}
        aria-hidden={!isDropdownOpen}
        isExpanded={isDropdownOpen}
        top={(navHeaderRef?.current as HTMLDivElement)?.clientHeight}
        onClick={() => closeHeader()}
      />

      {isUserAgent && <AgentHeader />}

      <styles.HeaderBar data-testid={TEST_ID.HEADER_BAR} aria-label={headerMenuContent.a11ySite}>
        <styles.StyledContainer>
          <styles.LogoWrapper
            href={headerMenuContent.belongHome.href}
            data-testid={TEST_ID.BUTTON_HOME}
            onClick={() => closeHeader()}
          >
            <BelongLogo />
            <A11yCopy>{headerMenuContent.belongHome.label}</A11yCopy>
          </styles.LogoWrapper>
          <styles.LinkWrapper>
            <styles.LeftLinks>
              {visibleHeaderMenuItems.map((menuItem, idx) => {
                const hasNavGroup = menuItem.navGroup1 || menuItem.navGroup2;

                return (
                  <Fragment key={menuItem.identifier}>
                    <HeaderBarButton
                      aria-expanded={
                        hasNavGroup && dropdownContainer === CONTAINERS.CONTAINER_MAIN
                          ? menuContent === menuItem && isDropdownOpen
                          : undefined
                      }
                      aria-current={
                        currentUrl !== undefined && currentUrl === removeTrailingSlash(menuItem.url?.href)
                          ? 'page'
                          : undefined
                      }
                      data-testid={parseMenuItemLabel(menuItem.parentLabel)}
                      label={menuItem.parentLabel}
                      onClick={() => (hasNavGroup ? handleEventMenuWithChildren(idx, menuItem) : closeHeader())}
                      onBlur={() => {
                        if (pressedKeys.current[pressedKeys.current.length - 1] === 'Shift+Tab') {
                          closeHeader({ previousMenuItem: true });
                        }
                      }}
                      onKeyDown={onInputKeyDown}
                      onFocus={() => {
                        if (pressedKeys.current[pressedKeys.current.length - 1] === 'Enter' && isDropdownOpen) {
                          setIsDropdownOpen(false);
                        }

                        pressedKeys.current = [];
                      }}
                      ref={menuItemRef.current[idx]}
                      iconName={hasNavGroup ? ICON_NAMES.ChevronDown : undefined}
                      iconRight
                      rotateIconOnHover
                      id={parseMenuItemLabel(menuItem.parentLabel)}
                      aria-controls={hasNavGroup ? `${parseMenuItemLabel(menuItem.parentLabel)}-a11y-id` : undefined}
                      href={menuItem.url ? menuItem.url.href : undefined}
                      isRightLink={visibleHeaderMenuItems.length === idx + 1}
                    />

                    {dropdownContainer === CONTAINERS.CONTAINER_MAIN &&
                      currentMenuItemIdx &&
                      currentMenuItemIdx.current === idx && (
                        <DropdownWrapper
                          isExpanded={isDropdownOpen}
                          data-testid={TEST_ID.DROPDOWN_WRAPPER}
                          aria-labelledby={menuItemID}
                          id={`${menuItemID}-a11y-id`}
                        >
                          <HeaderDropdown
                            headerMenuContent={headerMenuContent}
                            dropdownMenuContent={menuContent}
                            currentUrl={currentUrl}
                            dropdownWrapper={dropdownContainer}
                            onLinkClick={closeHeader}
                            hideMyAccountLinks={hideMyAccountLinks}
                            urls={urls}
                            onBlur={onLastLinkBlur}
                            onKeyDown={onInputKeyDown}
                          />
                        </DropdownWrapper>
                      )}
                  </Fragment>
                );
              })}
              <>
                <HeaderBarButton
                  aria-current={currentUrl === 'go/search' ? 'page' : undefined}
                  aria-expanded={dropdownContainer === CONTAINERS.CONTAINER_SEARCH && isDropdownOpen}
                  aria-label={headerMenuContent.a11ySearchMenu}
                  data-testid={TEST_ID.BUTTON_SEARCH}
                  label={headerMenuContent.searchMenuLabel}
                  iconName={ICON_NAMES.Search}
                  onClick={() => handleSearchButtonClick(menuItemRef.current.length - 1)}
                  ref={menuItemRef.current[menuItemRef.current.length - 1]} // it's the last one added to the ref array
                  id={parseMenuItemLabel(headerMenuContent.searchMenuLabel)}
                  aria-controls={SEARCH_PANEL_A11Y_ID}
                  onBlur={() => {
                    if (pressedKeys.current[pressedKeys.current.length - 1] === 'Shift+Tab') {
                      closeHeader({ previousMenuItem: true });
                    }
                  }}
                  onKeyDown={event => {
                    const { key } = event;
                    if (event.getModifierState(KEY_MODIFIER) && key === KEYBOARD_KEYS.TAB) {
                      closeHeader();
                    }
                  }}
                />
                {dropdownContainer === CONTAINERS.CONTAINER_SEARCH && (
                  <DropdownWrapper
                    isExpanded={isDropdownOpen}
                    data-testid={TEST_ID.DROPDOWN_WRAPPER}
                    aria-labelledby={SEARCH_TITLE_A11Y_ID}
                    id={SEARCH_PANEL_A11Y_ID}
                  >
                    <styles.StyledSearchContainer>
                      <SearchPanel
                        callback={() => closeHeader()}
                        data-testid="desktop-search-panel"
                        id={SEARCH_TITLE_A11Y_ID}
                      />
                    </styles.StyledSearchContainer>
                    <AccentStrip isBottom />
                  </DropdownWrapper>
                )}
              </>
              <NbnCartButton
                id={parseMenuItemLabel(headerMenuContent.a11yNbnCart)}
                a11yLabel={headerMenuContent.a11yNbnCart}
                cartIcon={headerMenuContent.nbnCartIcon}
                menuLabel={headerMenuContent.nbnCartMenuLabel}
                isNbnOrderCartReady={isNbnOrderCartReady}
                isDesktop
                data-testid={TEST_ID.BUTTON_NBN_CART}
                onFocus={() => {
                  if (isDropdownOpen) {
                    setIsDropdownOpen(false);
                  }
                }}
              />
              {headerMenuContent && isLoggedIn ? (
                <>
                  <HeaderBarButton
                    aria-current={currentUrl === urls.login ? 'page' : undefined}
                    aria-expanded={dropdownContainer === CONTAINERS.CONTAINER_ACCOUNT && isDropdownOpen}
                    aria-controls={MY_ACCOUNT_PANEL_A11Y_ID}
                    label={headerMenuContent.myAccountLabel}
                    data-testid={TEST_ID.BUTTON_DROPDOWN_MY_ACCOUNT}
                    iconName={ICON_NAMES.ChevronDown}
                    onClick={() => (isDropdownOpen ? closeHeader() : handleEventAccountMenu())}
                    onFocus={() => {
                      if (isDropdownOpen) {
                        setIsDropdownOpen(false);
                      }
                    }}
                    ref={accountMenuButtonRef}
                    id={parseMenuItemLabel(headerMenuContent.loginLabel)}
                    rotateIconOnHover
                    iconRight
                  />
                  {dropdownContainer === CONTAINERS.CONTAINER_ACCOUNT && (
                    <DropdownWrapper
                      isExpanded={isDropdownOpen}
                      data-testid={TEST_ID.DROPDOWN_WRAPPER}
                      aria-labelledby={parseMenuItemLabel(headerMenuContent.loginLabel)}
                      id={MY_ACCOUNT_PANEL_A11Y_ID}
                    >
                      <HeaderDropdown
                        headerMenuContent={headerMenuContent}
                        dropdownMenuContent={menuContent}
                        currentUrl={currentUrl}
                        dropdownWrapper={dropdownContainer}
                        onLinkClick={closeHeader}
                        hideMyAccountLinks={hideMyAccountLinks}
                        urls={urls}
                        onBlur={onLastLinkBlur}
                        onKeyDown={onInputKeyDown}
                      />
                    </DropdownWrapper>
                  )}
                </>
              ) : (
                headerMenuContent.loginLabel && (
                  // login button
                  <HeaderBarButton
                    aria-current={currentUrl === urls.login ? 'page' : undefined}
                    aria-label={headerMenuContent.a11yLogInLink}
                    data-testid={TEST_ID.BUTTON_LOGIN}
                    href={urls.login}
                    iconName={headerMenuContent.loginIcon?.icon}
                    label={headerMenuContent.loginLabel}
                    ref={accountMenuButtonRef}
                    id={parseMenuItemLabel(headerMenuContent.loginLabel)}
                  />
                )
              )}
            </styles.LeftLinks>
          </styles.LinkWrapper>
        </styles.StyledContainer>
      </styles.HeaderBar>
    </styles.Root>
  );
};

GlobalHeaderDesktop.displayName = 'GlobalHeaderDesktop';
export default GlobalHeaderDesktop;
